import { Typography } from "@/components";
import useBackward from "@/hooks/useBackward";
import { cn } from "@/libraries/cn";
import KeyboardBackspace from "@mui/icons-material/KeyboardBackspace";
import { type TypographyProps, colors } from "@mui/material";
import AppBar from "@mui/material/AppBar";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import type React from "react";

interface BackAppBarProps {
	to?: string;
	title?: string;
	subTitle?: string;
	titleClassName?: TypographyProps["className"];
	position?: "fixed" | "sticky" | "absolute" | "static" | "relative";
	bgColor?: string;
	className?: string;
	toolbarClassName?: string;
	rightContent?: React.ReactNode;
	beforeBack?: () => void;
	onBack?: () => void;
}

const BackAppBar: React.FC<React.PropsWithChildren<BackAppBarProps>> = ({
	children,
	to,
	title,
	subTitle,
	titleClassName,
	position = "sticky",
	className,
	rightContent,
	toolbarClassName,
	bgColor = colors.common.white,
	beforeBack,
	onBack,
}) => {
	const onBackward = useBackward();

	const onClickBack = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		beforeBack?.();
		if (onBack) {
			return onBack();
		}
		return onBackward(to);
	};

	return (
		<AppBar
			position={position}
			color="transparent"
			className={className}
			sx={{ boxShadow: "none", backgroundColor: `${bgColor} !important` }}
		>
			<div className="container">
				<Toolbar className={toolbarClassName} classes={{ root: "!p-0" }}>
					<IconButton aria-label="Back" onClick={onClickBack}>
						<KeyboardBackspace className="text-dark" />
					</IconButton>
					{title ? (
						<Typography
							fontWeight={600}
							fontSize={14}
							className={cn(titleClassName, {
								"flex flex-col": subTitle,
							})}
						>
							{title}
							{subTitle ? (
								<Typography
									fontWeight={400}
									fontSize={12}
									component="span"
									className="text-gray-500"
								>
									{subTitle}
								</Typography>
							) : null}
						</Typography>
					) : null}
					{rightContent}
				</Toolbar>
			</div>
			{children}
		</AppBar>
	);
};

export default BackAppBar;
